/**
 * 自动刷新的阻塞登记表。
 *
 * 组件在"不能被打断"的区间里 hold 一个 reason，结束后 release；自动刷新只在没有任何
 * HARD 阻塞、且（非隐藏标签页时）没有任何 SOFT 阻塞、用户也空闲时才执行。
 *
 * 两级语义：
 * - 'hard'：绝不在用户可能正在处理事情的瞬间刷新。到 hardBlockMaxMs 才由兜底强制越过，
 *   否则一个"开着不动的告警弹窗"会让版本永远不更新。
 * - 'soft'：用户空闲时可以被越过（例如后台的 AI 流式会话），配合 cap 使用。
 *
 * 用引用计数而非布尔，是因为 watch 的 immediate、重复 openModal、组件重建都会重复 hold；
 * 组件用 useRefreshBlocker（见 /@/hooks/web/useRefreshBlocker）接入，自动成对。
 */

export type BlockLevel = 'soft' | 'hard';

export type BlockReason =
  /** 告警弹窗打开（HARD）——护士可能正在处置告警 */
  | 'alarm-modal'
  /** 正在语音播报（HARD）——reload 不会取消浏览器级 speechSynthesis，会叠播 */
  | 'speech'
  /** 表单提交/响应在途（HARD）——reload 会丢掉这个请求的结果 */
  | 'form-inflight'
  /** 报警音循环播放中（HARD） */
  | 'alarm-audio'
  /** AI 流式会话（SOFT）——可被 cap 越过 */
  | 'ai-chat';

interface Holder {
  level: BlockLevel;
  count: number;
  since: number;
}

const holders = new Map<BlockReason, Holder>();

/** 累计 hold 次数，仅用于"接入是否还生效"的自检（见 getGuardStats） */
let holdEvents = 0;

export function holdRefresh(reason: BlockReason, level: BlockLevel = 'soft'): void {
  holdEvents += 1;

  const existing = holders.get(reason);
  if (existing) {
    existing.count += 1;
    // 只升不降：同一 reason 被不同来源按不同级别 hold 时，听更严格的那个
    if (level === 'hard') existing.level = 'hard';
    return;
  }

  holders.set(reason, { level, count: 1, since: Date.now() });
}

export function releaseRefresh(reason: BlockReason): void {
  const existing = holders.get(reason);
  if (!existing) return;

  existing.count -= 1;
  if (existing.count <= 0) {
    holders.delete(reason);
  }
}

export function hasHardBlocker(): boolean {
  for (const holder of holders.values()) {
    if (holder.level === 'hard') return true;
  }
  return false;
}

/**
 * 是否存在 SOFT 阻塞。
 *
 * 用途与 HARD 不同：HARD 是"任何时刻都别刷"，SOFT 只是"别走空闲路径"——
 * 用户可能确实空闲（例如 AI 正在长流式输出、人已走开），但这时候刷新会把他
 * 正在等的结果冲掉。超过 capMs 之后 SOFT 就不再是障碍（那是"最长等待上限"的语义）。
 */
export function hasSoftBlocker(): boolean {
  for (const holder of holders.values()) {
    if (holder.level === 'soft') return true;
  }
  return false;
}

/**
 * 最"老"的那个 HARD 阻塞的起始时刻。
 * 用于 hardBlockMaxMs 兜底：某个 HARD 阻塞被长期持有（组件没释放、或语音无限循环播放）时，
 * 不能让版本永远不更新。
 */
export function getOldestHardBlockerSince(): number | null {
  let oldest: number | null = null;
  for (const holder of holders.values()) {
    if (holder.level !== 'hard') continue;
    if (oldest === null || holder.since < oldest) oldest = holder.since;
  }
  return oldest;
}

export function getBlockers(): Array<{
  reason: BlockReason;
  level: BlockLevel;
  count: number;
  since: number;
}> {
  return Array.from(holders.entries()).map(([reason, holder]) => ({ reason, ...holder }));
}

/** 兜底清空（强制刷新路径）。正常路径下组件卸载会自己 release，不该走到这里 */
export function clearAllBlockers(): void {
  holders.clear();
}

/**
 * 通用弹窗兜底网：把"任何可见的 ant-design-vue Modal"当作 SOFT 阻塞。
 *
 * 一处代码覆盖全仓所有弹窗，兜住将来漏接的（签署、导入、报表…）。
 * 它**不替代**显式接入：告警弹窗那条是 HARD，语义更强、可读性也更好。
 */
export function hasVisibleDialog(): boolean {
  const wraps = Array.from(document.querySelectorAll('.ant-modal-wrap'));
  return wraps.some((wrap) => getComputedStyle(wrap as HTMLElement).display !== 'none');
}

/**
 * 自检：holdEvents 长期为 0 说明接入点被改坏（例如 WarningBoxModal 换成别的弹窗实现），
 * HARD 保护会静默退化成 SOFT 兜底网。让这种失效可观测。
 */
export function getGuardStats(): { holdEvents: number; current: number } {
  return { holdEvents, current: holders.size };
}
